import type { FieldMappingOverride, WidgetFieldMapping } from '../types';
import { extractChartContract } from '../types';
import { detectValueFields } from './detectValueFields';

export interface ResolvedChartFields {
  categoryField: string;
  valueFields: string[];
}

/**
 * Resolución de campos en 4 capas (prioridad descendente):
 *
 * 1. preference.config_json.fieldMapping (override del usuario)
 * 2. definition.field_mapping.category/values (estándar semántico v2)
 * 3. definition.config_schema (contrato legacy - backward compat)
 * 4. detectValueFields(data) (heurística fallback)
 */
export function resolveChartFields(
  configSchema: Record<string, unknown> | undefined | null,
  data: Record<string, unknown>[],
  userOverride?: FieldMappingOverride | null,
  widgetFieldMapping?: WidgetFieldMapping | null,
): ResolvedChartFields {
  // Calcular base desde el contrato del widget
  let base: ResolvedChartFields;

  if (widgetFieldMapping?.category || widgetFieldMapping?.values?.length) {
    // Capa 2: field_mapping semántico (nuevo estándar)
    const cat = widgetFieldMapping.category ?? 'name';
    base = {
      categoryField: cat,
      valueFields: widgetFieldMapping.values?.length
        ? widgetFieldMapping.values
        : detectValueFields(data, cat),
    };
  } else {
    // Capa 3: config_schema legacy → heurística
    const contract = extractChartContract(configSchema);
    base = contract
      ? { categoryField: contract.categoryField, valueFields: contract.valueFields }
      : { categoryField: 'name', valueFields: detectValueFields(data, 'name') };
  }

  // Capa 1: override del usuario (tiene prioridad sobre todo)
  if (userOverride) {
    return {
      categoryField: userOverride.category ?? base.categoryField,
      valueFields: userOverride.values?.length ? userOverride.values : base.valueFields,
    };
  }

  return base;
}

/**
 * Resuelve el label de visualización de un campo.
 * Prioridad: aliases del usuario → aliases del widget → nombre técnico del campo.
 */
export function resolveLabel(
  fieldKey: string,
  widgetAliases?: Record<string, string>,
  userAliases?: Record<string, string>,
): string {
  return userAliases?.[fieldKey] ?? widgetAliases?.[fieldKey] ?? fieldKey;
}
